import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import * as eventsMonitorEffects from '../pages/network-monitoring/events-monitor/store/events-monitor.effects';
import { eventsMonitorFeature } from '../pages/network-monitoring/events-monitor/store/events-monitor.reducer';
import { StartPageNameResolver } from '../pages/redirect/start-page-name.resovler';
import { authGuard } from '../services/guards/auth.guard';
import { guestGuard } from '../services/guards/guest.guard';
import { permissionGuard } from '../services/guards/permission.guard';
import { PermissionValue, ResourceValue } from '../services/permissions/permissions.types';
import { TitleTranslateResolver } from '../services/translate/title-translate.resolver';
import { ListingPageService } from '../shared/components/listing-page/listing-page.service';
import { QueryParamsHandlerService } from '../shared/components/listing-page/services/query-params-handler/query-params-handler.service';
import * as providers from './app.providers';
import { PortalPath, PortalRouteData, PortalRoutes, RouteItem } from './app.types';
import { customersRoutes } from './customers.routes';
import { devicesRoutes } from './devices.routes';
import { historyLogRoutes } from './history-log.routes';
import { reportsRoutes } from './reports.routes';
import { TranslateRegistry } from '../services/translate/translate.registry';
import { fieldOperationsLogRoutes } from './field-operations.routes';
import { issueMonitoringRoutes } from './issue-monitoring.routes';
import { eventsMonitorMapConfigProvider } from '../pages/network-monitoring/events-monitor/services/events-monitor-map-config.service';
import { SubscriptionListingFiltersSourceService } from '../pages/administration/subscription-management/subscription-listing-filters-adapter.service';
import { provideAlarmsListing } from '../pages/equipment/alarms/listing/alarms-listing.providers';
import { RequestProcessingListingFiltersSourceService } from '../pages/equipment/twoway-request-processing/services/request-processing-listing-filters-source.service';
import { dcuMtuCommunicationFeature } from '../pages/network-monitoring/dcu-to-mtu-communication/store/dcu-to-mtu-communication.reducer';
import * as dcuMtuCommunicationEffects from '../pages/network-monitoring/dcu-to-mtu-communication/store/dcu-to-mtu-communication.effect';
import { featureToggleKeys } from '../services/toggle/toggle.constants';
import { featureToggleGuard } from '../services/guards/feature-toggle.guard';

export const routesFactory = (): PortalRoutes => {
  return [
    {
      path: '',
      loadComponent: () =>
        import('../pages/redirect/landing-page-redirect-helper.component').then(
          c => c.LandingPageRedirectHelperComponent
        ),
      canActivate: [authGuard],
      resolve: { startPage: StartPageNameResolver }
    },
    customersRoutes,
    {
      path: PortalPath.totalConsumption,
      title: TitleTranslateResolver,
      loadComponent: () => import('../pages/csr/consumption-analytics/consumption-analytics.component'),
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.TOTAL_CONSUMPTION,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.TOTAL_CONSUMPTION,
          path: PortalPath.totalConsumption,
          icon: 'consumption-analytics',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.CSR,
          hasRight: {
            resourceValue: ResourceValue.CONSUMPTION_ANALYTICS,
            targetPermissionValue: PermissionValue.VIEW
          }
        })
      },
      canMatch: [authGuard, permissionGuard]
    },
    {
      path: PortalPath.eventsMonitor,
      title: TitleTranslateResolver,
      loadComponent: () =>
        import('../pages/network-monitoring/events-monitor/events-page.component').then(c => c.EventsPageComponent),
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.EVENTS_MONITOR,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.EVENTS_MONITOR,
          path: PortalPath.eventsMonitor,
          icon: 'view-dashboard-outline',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.NETWORK_MONITORING,
          hasRight: {
            resourceValue: ResourceValue.EVENT_MONITOR,
            targetPermissionValue: PermissionValue.VIEW
          }
        })
      },
      providers: [
        provideState(eventsMonitorFeature),
        provideEffects(eventsMonitorEffects),
        eventsMonitorMapConfigProvider
      ],
      canMatch: [authGuard, permissionGuard]
    },
    {
      path: PortalPath.mtuToDcuCommunication,
      title: TitleTranslateResolver,
      loadComponent: () =>
        import('../pages/network-monitoring/dcu-to-mtu-communication/dcu-to-mtu-communication.component'),
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.MTU_TO_DCU_COMMUNICATION,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.MTU_TO_DCU_COMMUNICATION,
          path: PortalPath.mtuToDcuCommunication,
          icon: 'mtu-dcu-communication',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.NETWORK_MONITORING,
          hasRight: {
            resourceValue: ResourceValue.MTU_TO_DCU_COMMUNICATION,
            targetPermissionValue: PermissionValue.VIEW
          }
        })
      },
      providers: [
        ListingPageService,
        QueryParamsHandlerService,
        provideState(dcuMtuCommunicationFeature),
        provideEffects(dcuMtuCommunicationEffects)
        // eventsMonitorMapConfigProvider
      ],
      canMatch: [authGuard, permissionGuard]
    },
    {
      path: 'leak-management',
      data: {
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.LEAK_MANAGEMENT,
          path: 'leak-management',
          icon: 'leak-management',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.NETWORK_MONITORING,
          isNonNavigable: true,
          expanded: true,
          hasRight: {
            resourceValue: ResourceValue.LEAK_MANAGEMENT,
            targetPermissionValue: PermissionValue.VIEW
          }
        })
      },
      children: [
        {
          path: 'acoustic-leak-detection',
          redirectTo: '/events-monitor',
          data: {
            routeItem: new RouteItem({
              title: TranslateRegistry.get.MAIN_MENU.TITLE.ACOUSTIC_LEAK_DETECTION,
              path: 'https://www.mykamstrup.com/en-us?returnUrl=https%3A%2f%2fwww.mykamstrup.com%2fen-us%2fdashboard',
              hasExternalLink: true,
              hasRight: {
                resourceValue: ResourceValue.ACOUSTIC_LEAK_DETECTION,
                targetPermissionValue: PermissionValue.VIEW
              }
            })
          }
        },
        {
          path: 'zone-scan',
          redirectTo: '/events-monitor',
          data: {
            routeItem: new RouteItem({
              title: TranslateRegistry.get.MAIN_MENU.TITLE.ZONE_SCAN,
              path: 'zone-scan',
              hasExternalLink: true,
              hasRight: {
                resourceValue: ResourceValue.ZONE_SCAN,
                targetPermissionValue: PermissionValue.VIEW
              }
            })
          }
        }
      ],
      canMatch: [authGuard, permissionGuard]
    },
    {
      path: PortalPath.healthMonitor,
      title: TitleTranslateResolver,
      loadComponent: () =>
        import('../pages/equipment/health-monitor/health-monitor.component').then(c => c.HealthMonitorComponent),
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.HEALTH_MONITOR,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.HEALTH_MONITOR,
          path: PortalPath.healthMonitor,
          icon: 'health_dashboard',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.EQUIPMENT,
          hasRight: {
            resourceValue: ResourceValue.HEALTH_MONITOR,
            targetPermissionValue: PermissionValue.VIEW
          }
        })
      } as PortalRouteData,
      canMatch: [authGuard, permissionGuard]
    },
    devicesRoutes,
    {
      path: PortalPath.twoWayRequestProcessing,
      title: TitleTranslateResolver,
      loadComponent: () =>
        import('../pages/equipment/twoway-request-processing/components/request-processing-listing-page/request-processing-listing-page.component').then(
          c => c.RequestProcessingListingPageComponent
        ),
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.TWO_WAY_REQUEST_PROCESSING,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.TWO_WAY_REQUEST_PROCESSING,
          path: PortalPath.twoWayRequestProcessing,
          icon: 'table',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.EQUIPMENT,
          featureToggleKey: featureToggleKeys.TwoWayRequestCommandManagement,
          hasRight: {
            resourceValue: ResourceValue.TWO_WAY_COMMUNICATION,
            targetPermissionValue: PermissionValue.VIEW
          }
        }),
        hasListingLayout: true
      } as PortalRouteData,
      providers: [
        RequestProcessingListingFiltersSourceService,
        providers.requestProcessingListingModelProvider,
        ListingPageService,
        QueryParamsHandlerService
      ],
      canMatch: [authGuard, permissionGuard, featureToggleGuard]
    },
    {
      path: PortalPath.alarms,
      title: TitleTranslateResolver,
      loadComponent: () =>
        import('../pages/equipment/alarms/components/alarms-listing-page/alarms-listing-page.component').then(
          c => c.AlarmsListingPageComponent
        ),
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.ALARMS,
        routeItem: new RouteItem({
          path: PortalPath.alarms,
          title: TranslateRegistry.get.MAIN_MENU.TITLE.ALARMS,
          icon: 'chat_bubble_event',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.EQUIPMENT,
          hasRight: {
            resourceValue: ResourceValue.ALARMS,
            targetPermissionValue: PermissionValue.VIEW
          }
        }),
        hasListingLayout: true
      },
      providers: provideAlarmsListing(),
      canMatch: [authGuard, permissionGuard]
    },
    fieldOperationsLogRoutes,
    issueMonitoringRoutes,
    historyLogRoutes,
    reportsRoutes,
    {
      path: PortalPath.eventsConfiguration,
      title: TitleTranslateResolver,
      loadComponent: () =>
        import('../pages/administration/event-configuration/event-configuration.component').then(
          c => c.EventConfigurationListingPageComponent
        ),
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.EVENT_CONFIGURATION,
        routeItem: new RouteItem({
          path: PortalPath.eventsConfiguration,
          title: TranslateRegistry.get.MAIN_MENU.TITLE.EVENT_CONFIGURATION,
          icon: 'format-list-bulleted-square',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.ADMINISTRATION,
          hasRight: {
            resourceValue: ResourceValue.EVENT_CONFIGURATION,
            targetPermissionValue: PermissionValue.VIEW
          }
        }),
        hasListingLayout: true
      } as PortalRouteData,
      providers: [providers.eventConfigurationModelProvider, ListingPageService, QueryParamsHandlerService],
      canMatch: [authGuard, permissionGuard]
    },
    {
      path: PortalPath.subscriptionManagement,
      title: TitleTranslateResolver,
      loadComponent: () =>
        import('../pages/administration/subscription-management/subscription-management.component').then(
          c => c.SubscriptionManagementPageComponent
        ),
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.SUBSCRIPTION_MANAGEMENT,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.SUBSCRIPTION_MANAGEMENT,
          path: PortalPath.subscriptionManagement,
          icon: 'format-list-bulleted-square',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.ADMINISTRATION,
          hasRight: {
            resourceValue: ResourceValue.SUBSCRIPTION_MANAGEMENT,
            targetPermissionValue: PermissionValue.VIEW
          }
        }),
        hasListingLayout: true
      },
      providers: [
        SubscriptionListingFiltersSourceService,
        providers.subscriptionManagementModelProvider,
        ListingPageService,
        QueryParamsHandlerService
      ],
      canMatch: [authGuard, permissionGuard]
    },
    {
      path: PortalPath.reportGroups,
      title: TitleTranslateResolver,
      loadComponent: () =>
        import('../pages/administration/report-groups/report-groups.component').then(c => c.ReportGroupsPageComponent),
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.REPORT_GROUPS,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.REPORT_GROUPS,
          path: PortalPath.reportGroups,
          icon: 'format-list-bulleted-square',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.ADMINISTRATION,
          hasRight: {
            resourceValue: ResourceValue.REPORT_GROUPS,
            targetPermissionValue: PermissionValue.VIEW
          }
        }),
        hasListingLayout: true
      },
      providers: [providers.reportGroupsModelProvider, ListingPageService, QueryParamsHandlerService],
      canMatch: [authGuard, permissionGuard]
    },
    {
      path: PortalPath.userRoles,
      title: TitleTranslateResolver,
      loadComponent: () => import('../pages/administration/user-roles/user-roles.component'),
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.USER_ROLES,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.USER_ROLES,
          path: PortalPath.userRoles,
          icon: 'format-list-bulleted-square',
          group: TranslateRegistry.get.MAIN_MENU.SECTION.ADMINISTRATION,
          hasRight: {
            resourceValue: ResourceValue.PERMISSIONS_MGT,
            targetPermissionValue: PermissionValue.VIEW
          }
        }),
        hasListingLayout: true
      },
      providers: [providers.userRolesModelProvider, ListingPageService, QueryParamsHandlerService],
      canMatch: [authGuard, permissionGuard]
    },
    {
      loadComponent: () =>
        import('../pages/help-and-settings/user-settings/user-settings-page/user-settings-page.component').then(
          c => c.UserSettingsPageComponent
        ),
      path: PortalPath.userSettings,
      title: TitleTranslateResolver,
      data: {
        titleTranslation: TranslateRegistry.get.MAIN_MENU.TITLE.USER_SETTINGS,
        routeItem: new RouteItem({
          title: TranslateRegistry.get.MAIN_MENU.TITLE.USER_SETTINGS
        })
      },
      canMatch: [authGuard]
    },
    {
      path: 'callback',
      // `CallbackComponent` does nothing but setting up auth.
      loadComponent: () => import('../pages/callback/callback.component').then(c => c.CallbackComponent)
    },
    {
      path: 'unauthorized',
      loadComponent: () => import('../pages/unauthorized/unauthorized.component').then(c => c.UnauthorizedComponent)
    },
    {
      path: 'realm',
      loadComponent: () => import('../pages/realm/realm.component').then(c => c.RealmComponent),
      data: {
        routeItem: new RouteItem({
          title: 'Sign In'
        }),
        hasListingLayout: true
      },
      canMatch: [guestGuard]
    },
    {
      path: '**',
      loadComponent: () =>
        import('../pages/redirect/landing-page-redirect-helper.component').then(
          c => c.LandingPageRedirectHelperComponent
        ),
      canActivate: [authGuard],
      resolve: { startPage: StartPageNameResolver }
    }
  ];
};
